import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API calls
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  getProfile: () => api.get('/api/auth/profile'),
  updateProfile: (userData) => api.put('/api/auth/profile', userData),
  changePassword: (passwordData) => api.post('/api/auth/change-password', passwordData),
  enrollBiometric: (biometricData) => api.post('/api/auth/enroll-biometric', biometricData),
  logout: () => api.post('/api/auth/logout'),
}

// Document API calls
export const documentAPI = {
  upload: (formData) => api.post('/api/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  list: () => api.get('/api/documents/list'),
  get: (id) => api.get(`/api/documents/${id}`),
  delete: (id) => api.delete(`/api/documents/${id}`),
  updateStatus: (id, statusData) => api.put(`/api/documents/${id}/status`, statusData),
  getPending: () => api.get('/api/documents/pending'),
  getStatistics: () => api.get('/api/documents/statistics'),
  bulkVerify: (data) => api.post('/api/documents/bulk-verify', data),
}

// Verification API calls
export const verificationAPI = {
  verifyDocument: (verificationData) => api.post('/api/verify/document', verificationData),
  verifyFace: (formData) => api.post('/api/verify/face', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  verifyDocumentOnly: (data) => api.post('/api/verify/document-only', data),
  checkStatus: (documentId) => api.get(`/api/verify/status/${documentId}`),
  batchVerify: (data) => api.post('/api/verify/batch', data),
}

// Map API calls
export const mapAPI = {
  getData: (params = {}) => api.get('/api/map/data', { params }),
  getHeatmap: () => api.get('/api/map/heatmap'),
  getHotspots: () => api.get('/api/map/hotspots'),
  getStatistics: () => api.get('/api/map/statistics'),
  search: (params) => api.get('/api/map/search', { params }),
  getActivityDetails: (activityId) => api.get(`/api/map/activity/${activityId}`),
  exportData: (format = 'json') => api.get('/api/map/export', { params: { format } }),
}

// Health check
export const healthAPI = {
  check: () => api.get('/api/health'),
}

export default api